import React, { useCallback, useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import ScheduleTask from '@totem/components/ticketing/details/tasks/scheduleTask';
import TaskContext from '@totem/components/ticketing/details/tasks/taskContext';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import {
  HierarchicalTask,
  Resources,
  Task,
} from '@totem/components/ticketing/ticketing';
import {
  buildTaskHierarchy,
  createTaskMap,
  getDefaultTaskWorkflowSteps,
  getNextRootPosition,
  getNextTask,
  getPreviousTask,
  sortTasksForUIDisplay,
} from '@totem/components/ticketing/utilities';
import NaPartyInvolvedContext from '@totem/components/unauthenticated/ticketing/details/partyInvolved/naPartyInvolvedContext';
import { isNotNull } from '@totem/utilities/common';
import { UNAUTHENTICATED_TICKET_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import {
  sortNumberAscending,
  sortNumberDescending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

type Props = {
  children?: React.ReactNode;
};

const NaTaskContainer = ({ children }: Props) => {
  const { ticket, onAction } = useContext(TicketContext);
  const { token } = useContext(NaPartyInvolvedContext);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [taskAction, setTaskAction] = useState<string>('');
  const [taskActionExtras, setTaskActionExtras] = useState<string>('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activeTasks, setActiveTasks] = useState<Task[]>([]);
  const [taskHierarchy, setTaskHierarchy] = useState<HierarchicalTask[]>([]);
  const [taskMap, setTaskMap] = useState<Map<number, HierarchicalTask>>(
    new Map<number, HierarchicalTask>(),
  );
  const [resources, setResources] = useState<Resources>([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<Task>(null);

  useEffect(() => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.tasks)
    ) {
      const newTasks = ticket.ticket.tasks
        .filter((chk) => !chk.isDeleted)
        .sort((compA, compB) =>
          sortNumberAscending(compA.number, compB.number),
        );

      const newActiveTask = sortTasksForUIDisplay(newTasks);
      const newTaskHierarchy = buildTaskHierarchy(newActiveTask);
      const newTaskMap = createTaskMap(newActiveTask);

      setTasks(newTasks);

      setActiveTasks(newActiveTask);

      setTaskHierarchy(newTaskHierarchy);

      setTaskMap(newTaskMap);
    } else {
      setTasks([]);

      setActiveTasks([]);

      setTaskHierarchy([]);
    }
  }, [ticket?.ticket?.tasks]);

  useEffect(() => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.partiesInvolved)
    ) {
      const res = ticket.ticket.partiesInvolved
        .map((pi) => {
          return {
            id: pi.id,
            name: pi.name,
            email: pi.email,
          };
        })
        .sort((compA, compB) => sortStringAscending(compA.name, compB.name));
      setResources(res);
    } else {
      setResources([]);
    }
  }, [ticket?.ticket?.partiesInvolved]);

  const sendTask = useCallback(
    async (payload: Task) => {
      if (!isSending) {
        fetch(`${UNAUTHENTICATED_TICKET_ENDPOINT}/task`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            onAction('refresh', '');
          }
        });
      }
    }, // End async
    [],
  );

  const getPrevious = (taskNumber: number) => {
    return getPreviousTask(taskNumber, taskMap, tasks);
  };

  const getNext = (taskNumber: number) => {
    return getNextTask(taskNumber, taskMap, tasks);
  };

  const getTask = (taskNumber: number): Task => {
    const tsk = activeTasks.find((task) => task.number === taskNumber);
    if (isNotNull(tsk)) {
      return tsk;
    }
    return null;
  };

  const getPreviousSiblings = (refTask: Task): Task[] => {
    if (isNotNull(refTask)) {
      const previousSiblings = activeTasks
        .filter(
          (chk) =>
            chk.parent === refTask.parent && chk.position < refTask.position,
        )
        .sort((compA, compB) =>
          sortNumberDescending(compA.position, compB.position),
        );
      return previousSiblings;
    }
    return [];
  };

  const getPreviousSibling = (refTask: Task): Task => {
    if (isNotNull(refTask)) {
      const previousSiblings = getPreviousSiblings(refTask);
      if (previousSiblings.length > 0) {
        return previousSiblings[0];
      }
    }
    return null;
  };

  const getNextSiblings = (refTask: Task): Task[] => {
    if (isNotNull(refTask)) {
      const nextSiblings = activeTasks
        .filter(
          (chk) =>
            chk.parent === refTask.parent && chk.position > refTask.position,
        )
        .sort((compA, compB) =>
          sortNumberAscending(compA.position, compB.position),
        );
      return nextSiblings;
    }
    return [];
  };

  const getNextSibling = (refTask: Task): Task => {
    if (isNotNull(refTask)) {
      const nextSiblings = getNextSiblings(refTask);
      if (nextSiblings.length > 0) {
        return nextSiblings[0];
      }
    }
    return null;
  };

  const getNextPositionByParent = (parent: number): number => {
    const childTasks = activeTasks
      .filter((chk) => chk.parent === parent)
      .sort((compA, compB) =>
        sortNumberDescending(compA.position, compB.position),
      );
    if (isNotNull(childTasks) && childTasks.length > 0) {
      return childTasks[0].position + 1;
    }

    return 0;
  };

  const handleStatusChange = (newStatus: string, refTask: Task) => {
    switch (newStatus) {
      case 'SCHEDULED':
        setSelectedTask(refTask);

        setShowScheduleDialog(true);
        break;
      case 'IN PROGRESS':
        {
          const startDate = dayjs().toISOString();
          const payload = {
            ...refTask,
            status: newStatus,
            actualStartDate: startDate,
          };
          sendTask(payload);
        }
        break;
      case 'COMPLETED':
        {
          const endDate = dayjs().toISOString();
          const payload = {
            ...refTask,
            status: newStatus,
            actualEndDate: endDate,
          };
          sendTask(payload);
        }
        break;
      default:
        {
          const payload = { ...refTask, status: newStatus };
          sendTask(payload);
        }
        break;
    }
  };

  const handleAction = (newAction: string, newExtras: string) => {
    switch (newAction) {
      default:
        setTaskAction(newAction);

        setTaskActionExtras(newExtras);
    }
  };

  return (
    <TaskContext.Provider
      value={{
        taskAction,
        taskActionExtras,
        onTaskAction: handleAction,
        rawTasks: tasks,
        tasks: activeTasks,
        taskHierarchy,
        taskMap,
        resources,
        taskWorkflow: getDefaultTaskWorkflowSteps(),
        getTask,
        getPreviousSibling,
        getPreviousSiblings,
        getNextSibling,
        getNextSiblings,
        getPreviousTask: getPrevious,
        getNextTask: getNext,
        getNextRootPosition: () => getNextRootPosition(ticket),
        getNextPositionByParent,
        onTaskStatusChange: handleStatusChange,
      }}
    >
      {children}
      {showScheduleDialog && (
        <ScheduleTask
          visible={showScheduleDialog}
          task={selectedTask}
          onCancel={() => {
            setShowScheduleDialog(false);
          }}
          onComplete={(newStartDate) => {
            setShowScheduleDialog(false);
            const payload = {
              ...selectedTask,
              status: 'SCHEDULED',
              expectedStartDate: newStartDate,
            };
            sendTask(payload);
          }}
        />
      )}
    </TaskContext.Provider>
  );
};

export default NaTaskContainer;
